import { useLayoutEffect, useRef, useState } from 'react';
import { PhoneIcon } from 'lucide-react';
import { stripToDigits, caretIndexAfterDigits } from '@noor/lib';
import { formatNationalPhone, toCanonicalPhone, LEGACY_PHONE_DIGITS } from '../lib/phone';

/**
 * Masked phone input — `+998 (90) 123 45 67`.
 *
 * The old panel used `react-imask` with the country table's `(00) 000 00 00`
 * template after a fixed `+998` prefix. That library is not a dependency here,
 * and the design system already ships the primitives (`applyMask`,
 * `stripToDigits`, `caretIndexAfterDigits`) its own `FormField.Phone` is built
 * from — so the mask is applied with those instead of adding a dependency for
 * one field.
 *
 * The CANONICAL value is E.164 (`+998901234567`): the mask is presentation
 * only, and `onChange` always reports the canonical form. That matters because
 * this feeds an order payload — sending the formatted string would put spaces
 * and brackets in the courier's contact number.
 *
 * The caret is re-anchored after every reformat. Without it, typing in the
 * middle of a filled number jumps the cursor to the end on each keystroke,
 * which is the classic masked-input bug.
 */

interface LegacyPhoneInputProps {
  /** Canonical E.164 value, or '' when empty. */
  value: string;
  onChange: (e164: string) => void;
  'aria-label'?: string;
  /** Frozen — the edit screen's arrived-leg lock. */
  disabled?: boolean | undefined;
}

export function LegacyPhoneInput({
  value,
  onChange,
  'aria-label': ariaLabel,
  disabled,
}: LegacyPhoneInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  /** Digit count before the caret, captured on edit and restored after render. */
  const caretDigitsRef = useRef<number | null>(null);
  const [focused, setFocused] = useState(false);

  const formatted = formatNationalPhone(value);

  // Restore the caret after React rewrites the value. `useLayoutEffect` so it
  // happens before paint — in a plain effect the cursor visibly jumps.
  useLayoutEffect(() => {
    const target = caretDigitsRef.current;
    if (target === null || !inputRef.current) return;
    caretDigitsRef.current = null;
    const index = caretIndexAfterDigits(formatted, target);
    inputRef.current.setSelectionRange(index, index);
  }, [formatted]);

  return (
    <label className="flex h-12 flex-1 items-center gap-2 rounded-[96px] bg-[var(--lg-grey-100)] px-4">
      <PhoneIcon className="size-5 shrink-0 text-[var(--lg-grey-500)]" />
      <span className="shrink-0 text-[16px] font-medium text-[var(--lg-fg)]">+998</span>
      <input
        ref={inputRef}
        type="tel"
        inputMode="numeric"
        aria-label={ariaLabel}
        disabled={disabled}
        value={formatted}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        // The placeholder only shows while unfocused: once focused, an empty
        // field with a template placeholder reads as text the user must delete.
        placeholder={focused ? '' : '(__) ___ __ __'}
        onChange={(e) => {
          const raw = e.target.value;
          const caret = e.target.selectionStart ?? raw.length;
          // How many digits precede the caret — the anchor that survives
          // reformatting, unlike a character offset.
          caretDigitsRef.current = Math.min(
            stripToDigits(raw.slice(0, caret)).length,
            LEGACY_PHONE_DIGITS,
          );

          // Always reports E.164 (or '' when cleared) — see `lib/phone.ts`.
          onChange(toCanonicalPhone(raw));
        }}
        className="h-full w-full bg-transparent text-[16px] font-medium text-[var(--lg-fg)] outline-none placeholder:text-[var(--lg-grey-400)] disabled:cursor-not-allowed disabled:opacity-60"
      />
    </label>
  );
}
